import React, { forwardRef } from 'react';
import clsx from 'clsx';

import styles from './icon.module.scss';

type Size = 'sm' | 'md' | 'lg' | 'xl' | number | false;
type Color = 'inherit' | 'primary' | 'secondary' | 'success' | 'error';

export interface Props extends Omit<React.SVGProps<SVGSVGElement>, 'color' | 'children'> {
  type?: 'svg' | 'div';
  children?: React.ReactNode | React.ReactNode[];
  color?: Color;
  size?: Size;
  viewBox?: string;
  title?: string;
  className?: string;
}

export const Icon = forwardRef<SVGSVGElement | HTMLDivElement, Props>((props, ref) => {
  const {
    children,
    color = 'inherit',
    size = 'md',
    viewBox = '0 0 24 24',
    className,
    style,
    type = 'svg',
    ...rest
  } = props;

  const isNumericSize = typeof size === 'number';

  const composedClassName = clsx(
    styles['icon'],
    styles[`icon--color-${color}`],
    !isNumericSize && size && styles[`icon--size-${size}`],
    type === 'div' && styles['icon--as-div'],
    className
  );

  const composedStyle = isNumericSize ? { fontSize: `${size}px`, ...style } : style;

  if (type === 'div') {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={composedClassName}
        style={composedStyle}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    );
  }

  return (
    <svg
      ref={ref as React.Ref<SVGSVGElement>}
      viewBox={viewBox}
      className={composedClassName}
      style={composedStyle}
      {...rest}
    >
      {children}
    </svg>
  );
});

Icon.displayName = 'Icon';

export default React.memo(Icon);
